/**
 * Inline click-to-edit atoms for the details sidebar (W1, WYSIWYG plan §7).
 * Same interaction grammar as DocumentsPane's MetaField: the field IS the
 * input (transparent until focus), local draft seeded from the live value,
 * Enter commits (blur), Escape reverts, blur commits — commit fires only on
 * change. Presentation only; writing goes through the caller's onCommit
 * (the mutation service), never a raw store write.
 */
import { useEffect, useRef, useState, type ReactElement } from "react";

export interface InlineTextProps {
  readonly value: string;
  readonly onCommit: (next: string) => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  /** Tooltip explaining a disabled control (e.g. "inherited from type"). */
  readonly disabledReason?: string;
  readonly mono?: boolean;
  readonly className?: string;
  readonly ariaLabel: string;
  /** Datalist options (e.g. the type registry for the kind field). */
  readonly options?: readonly string[];
}

export function InlineText(props: InlineTextProps): ReactElement {
  const [draft, setDraft] = useState(props.value);
  useEffect(() => setDraft(props.value), [props.value]);
  const listId = useRef(props.options ? `il-${Math.random().toString(36).slice(2, 9)}` : undefined);

  const commit = (): void => {
    const next = draft.trim();
    if (next !== props.value) props.onCommit(next);
  };

  return (
    <>
      <input
        aria-label={props.ariaLabel}
        className={
          (props.mono ? "font-mono " : "") +
          "bg-transparent border border-transparent rounded-sm px-1 -mx-1 min-w-0 " +
          "focus:border-slate-600 focus:bg-slate-800/(--tint-dim) focus:outline-hidden " +
          "disabled:cursor-not-allowed " +
          (props.className ?? "")
        }
        value={draft}
        placeholder={props.placeholder}
        disabled={props.disabled}
        title={props.disabled ? props.disabledReason : undefined}
        list={listId.current}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            setDraft(props.value);
            e.currentTarget.blur();
          }
        }}
      />
      {props.options && listId.current && (
        <datalist id={listId.current}>
          {props.options.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>
      )}
    </>
  );
}

export interface InlineTextareaProps {
  readonly value: string;
  readonly onCommit: (next: string) => void;
  readonly placeholder?: string;
  readonly ariaLabel: string;
  readonly className?: string;
}

/** Multiline variant (description). Commit on blur; Escape reverts; Enter
 *  inserts a newline (multiline content is the point here). */
export function InlineTextarea(props: InlineTextareaProps): ReactElement {
  const [draft, setDraft] = useState(props.value);
  useEffect(() => setDraft(props.value), [props.value]);

  const commit = (): void => {
    const next = draft.replace(/\s+$/, "");
    if (next !== props.value) props.onCommit(next);
  };

  return (
    <textarea
      aria-label={props.ariaLabel}
      className={
        "w-full bg-transparent border border-slate-700/(--tint-dim) rounded-sm px-2 py-1 text-sm " +
        "text-slate-300 leading-snug resize-y min-h-16 " +
        "focus:border-slate-600 focus:bg-slate-800/(--tint-dim) focus:outline-hidden " +
        (props.className ?? "")
      }
      value={draft}
      placeholder={props.placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          setDraft(props.value);
          e.currentTarget.blur();
        }
      }}
    />
  );
}
